import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Wallet, TrendingUp, Clock, CheckCircle2,
  Phone, Loader2, BookOpen, ArrowRight, Info, BarChart3,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useGetInstructorFinanceOverviewQuery,
  useGetInstructorSalesQuery,
  useGetInstructorPayoutHistoryQuery,
  useRequestInstructorPayoutMutation,
} from '../../api/apiSlice';
import Loading from '../../components/common/Loading';
import Pagination from '../../components/common/Pagination';

// В финансовых контекстах "0 ₸" — это реальная сумма (0 продаж, 0 выплат),
// а не "бесплатный курс". Дефолтный fmt() из coursePricing.js на 0
// возвращает "Бесплатно", что вводит в заблуждение → локальный formatter.
const fmt = (n) => `${(Number(n) || 0).toLocaleString('ru-RU')} ₸`;

// Лейблы статусов через t() — резолвится в payouts.map() при рендере.
const PAYOUT_STATUS_META = {
  pending: { key: 'payoutStatusPending', color: 'amber' },
  paid: { key: 'payoutStatusPaid', color: 'emerald' },
  rejected: { key: 'payoutStatusRejected', color: 'red' },
};

const COLOR_CLASSES = {
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300',
  emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300',
  red: 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300',
};

function StatCard({ label, value, hint, icon: Icon, color = 'indigo' }) {
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
          <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
            {value}
          </div>
          {hint && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</div>}
        </div>
        {Icon && (
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center bg-${color}-50 dark:bg-${color}-950/40 text-${color}-600 dark:text-${color}-400`}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </div>
  );
}

function PayoutRequestModal({ open, overview, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState(overview?.savedKaspiPhone || '');
  const [notes, setNotes] = useState('');
  const [savePhone, setSavePhone] = useState(true);
  const [requestPayout, { isLoading }] = useRequestInstructorPayoutMutation();

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    if (!/^\+?\d{10,15}$/.test(cleanPhone)) {
      toast.error(t('instructorFinance.modalErrorPhone'));
      return;
    }
    try {
      await requestPayout({ kaspiPhone: cleanPhone, notes: notes.trim() || undefined, savePhone }).unwrap();
      toast.success(t('instructorFinance.modalSuccess'));
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.data?.message || t('instructorFinance.modalErrorSubmit'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('instructorFinance.modalTitle')}</h3>

          <div className="rounded-2xl bg-indigo-50 dark:bg-indigo-950/30 px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-indigo-900 dark:text-indigo-200">{t('instructorFinance.modalAmount')}</span>
            <span className="text-xl font-bold text-indigo-700 dark:text-indigo-300 tabular-nums">
              {fmt(overview?.availableBalance || 0)}
            </span>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('instructorFinance.modalPhone')}</span>
            <div className="mt-1.5 relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+7 707 123 45 67"
                className="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
              />
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={savePhone} onChange={(e) => setSavePhone(e.target.checked)} className="h-5 w-5 rounded border-gray-300 text-indigo-600" />
            <span className="text-sm text-gray-700 dark:text-gray-200">{t('instructorFinance.modalSavePhone')}</span>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('instructorFinance.modalNotes')}</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} className="mt-1.5 w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm focus:border-indigo-500 outline-none resize-none" />
          </label>

          <div className="rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200/90">
            {t('instructorFinance.modalWarning')}
          </div>

          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 rounded-2xl border border-gray-300 dark:border-gray-700 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">{t('instructorFinance.modalCancel')}</button>
            <button type="submit" disabled={isLoading} className="flex-1 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('instructorFinance.modalConfirm')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Серверная пагинация + фильтрация по дате-диапазону. Бэк возвращает items
// текущей страницы, total, pageCount + periodSummary (сумма за весь период,
// не только страницу — иначе цифра скакала бы при пагинации).
const SALES_PAGE_SIZE = 15;
const PAYOUTS_PAGE_SIZE = 10;

// yyyy-mm-dd для <input type="date"> и URL-query.
const fmtIso = (d) => {
  const z = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
};
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return fmtIso(d);
};
const today = () => fmtIso(new Date());

// Quick-presets — заполняют from/to одним кликом. Всё время = пустые даты.
// labelKey резолвится в t() при рендере, поэтому переключение языка работает.
const PRESETS = [
  { id: '7',   labelKey: 'instructorFinance.preset7',   getRange: () => ({ from: daysAgo(7),  to: today() }) },
  { id: '30',  labelKey: 'instructorFinance.preset30',  getRange: () => ({ from: daysAgo(30), to: today() }) },
  { id: '90',  labelKey: 'instructorFinance.preset90',  getRange: () => ({ from: daysAgo(90), to: today() }) },
  { id: 'all', labelKey: 'instructorFinance.presetAll', getRange: () => ({ from: '', to: '' }) },
];

export default function InstructorFinancePage() {
  const { t } = useTranslation();
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  // Дефолт — последние 30 дней. Пользователь меняет вручную через календарь
  // или через preset-кнопки.
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(today());
  const [salesPage, setSalesPage] = useState(1);
  const [payoutsPage, setPayoutsPage] = useState(1);

  const { data: overview, isLoading: loadingOverview, refetch: refetchOverview } = useGetInstructorFinanceOverviewQuery();
  const { data: salesData, isLoading: loadingSales } = useGetInstructorSalesQuery({
    from: dateFrom,
    to: dateTo,
    page: salesPage,
    limit: SALES_PAGE_SIZE,
  });
  const { data: payoutsData, isLoading: loadingPayouts } = useGetInstructorPayoutHistoryQuery({
    page: payoutsPage,
    limit: PAYOUTS_PAGE_SIZE,
  });

  // Reset страницы при смене дат — иначе окажешься на стр.3 при широком
  // интервале, сузил → осталась 1 страница, стр.3 пустая.
  useEffect(() => { setSalesPage(1); }, [dateFrom, dateTo]);

  // Какой preset активен сейчас? Сравниваем текущие даты с диапазонами
  // каждого preset'а. Если совпадает — подсвечиваем кнопку.
  const activePreset = (() => {
    for (const p of PRESETS) {
      const r = p.getRange();
      if (r.from === dateFrom && r.to === dateTo) return p.id;
    }
    return null;
  })();

  const applyPreset = (preset) => {
    const r = preset.getRange();
    setDateFrom(r.from);
    setDateTo(r.to);
  };

  // Серверные данные — никакого client-side filter/aggregation.
  const sales = salesData?.items || [];
  const periodSummary = salesData?.periodSummary || { total: 0, count: 0, refundedCount: 0 };
  const salesTotal = salesData?.total || 0;
  const salesPageCount = salesData?.pageCount || 1;

  if (loadingOverview) {
    return <Loading variant="card" label={t('instructorFinance.loading')} />;
  }

  const o = overview || {};
  const payouts = payoutsData?.items || [];
  const payoutsTotal = payoutsData?.total || 0;
  const payoutsPageCount = payoutsData?.pageCount || 1;

  const cooldownActive = o.nextPayoutAvailableAt && new Date(o.nextPayoutAvailableAt) > new Date();
  const belowMin = (o.availableBalance || 0) < (o.minPayoutAmount || 0);
  const canRequestPayout = !cooldownActive && !belowMin && (o.availableBalance || 0) > 0;

  const cooldownText = (() => {
    if (!cooldownActive) return null;
    const days = Math.ceil((new Date(o.nextPayoutAvailableAt) - Date.now()) / (24 * 3600 * 1000));
    return t('instructorFinance.cooldownDay', { count: days });
  })();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('instructorFinance.title')}</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('instructorFinance.subtitle')}
        </p>
      </div>

      {/* Объяснение разницы между Финансы и Аналитикой — частый вопрос.
          Один компактный банер, ссылка на смежную страницу. */}
      <div className="rounded-2xl border border-indigo-200/70 dark:border-indigo-900/40 bg-indigo-50/50 dark:bg-indigo-950/20 p-4 flex items-start gap-3">
        <Info className="h-4 w-4 mt-0.5 text-indigo-600 dark:text-indigo-400 shrink-0" />
        <div className="text-sm text-gray-700 dark:text-gray-300 flex-1">
          <p>
            <strong className="text-gray-900 dark:text-white">{t('instructorFinance.infoP1Strong')}</strong>{t('instructorFinance.infoP1Text')}
          </p>
          <p className="mt-1">
            {t('instructorFinance.infoP2Prefix')} <strong>{t('instructorFinance.infoP2Bold')}</strong> {t('instructorFinance.infoP2Mid')} <Link to="/dashboard/instructor/analytics" className="text-indigo-600 dark:text-indigo-400 font-semibold inline-flex items-center gap-1 hover:underline">
              <BarChart3 className="h-3.5 w-3.5" /> {t('instructorFinance.infoAnalyticsLink')}
            </Link>.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          label={t('instructorFinance.totalEarned')}
          value={fmt(o.lifetimeEarned || 0)}
          hint={t('instructorFinance.salesCount', { count: o.lifetimeSales || 0 })}
          icon={TrendingUp}
          color="emerald"
        />
        <StatCard
          label={t('instructorFinance.alreadyPaid')}
          value={fmt(o.totalPaidOut || 0)}
          icon={CheckCircle2}
          color="indigo"
        />
        <StatCard
          label={t('instructorFinance.inProcess')}
          value={fmt(o.pendingBalance || 0)}
          hint={t('instructorFinance.inProcessHint')}
          icon={Clock}
          color="amber"
        />
      </div>

      {/* Available + Payout button */}
      <div className="rounded-3xl border border-indigo-200 dark:border-indigo-900/50 bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/30 dark:to-gray-900 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-white">
            <Wallet className="h-7 w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-300">{t('instructorFinance.available')}</div>
            <div className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white tabular-nums mt-1">
              {fmt(o.availableBalance || 0)}
            </div>
            {(cooldownText || belowMin) && (
              <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                {cooldownText || t('instructorFinance.belowMin', { amount: fmt(o.minPayoutAmount || 0) })}
              </div>
            )}
          </div>
          <button
            type="button"
            disabled={!canRequestPayout}
            onClick={() => setShowPayoutModal(true)}
            className="w-full sm:w-auto rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-3 text-sm shrink-0 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition flex items-center justify-center gap-2"
          >
            {t('instructorFinance.requestPayout')}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Sales feed + period filter */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-white">{t('instructorFinance.salesTitle')}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t('instructorFinance.salesSummary', { count: periodSummary.count, amount: fmt(periodSummary.total) })}
                {periodSummary.refundedCount > 0 && (
                  <span className="ml-1 text-red-600 dark:text-red-400">
                    {t('instructorFinance.salesRefunds', { count: periodSummary.refundedCount })}
                  </span>
                )}
              </p>
            </div>
          </div>
          {/* Period selector — date range picker с quick-presets.
              Preset подсвечивается только если текущие даты ТОЧНО совпадают
              с его диапазоном; иначе режим «свой период» (никто не активен).
              Бэк фильтрует по from/to, агрегаты считает на DB. */}
          <div className="space-y-2">
            <div className="flex gap-1 rounded-xl bg-gray-100 dark:bg-gray-800 p-1 w-full sm:w-fit">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className={`flex-1 sm:flex-initial rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                    activePreset === p.id
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {t(p.labelKey)}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                max={dateTo || undefined}
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                title={t('instructorFinance.dateFrom')}
              />
              <span className="text-gray-400">—</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                min={dateFrom || undefined}
                max={today()}
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                title={t('instructorFinance.dateTo')}
              />
              {(dateFrom || dateTo) && (
                <button
                  type="button"
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  className="text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  title={t('instructorFinance.resetTitle')}
                >
                  {t('instructorFinance.reset')}
                </button>
              )}
            </div>
          </div>
        </div>
        {loadingSales ? (
          <div className="p-8"><Loading variant="inline" /></div>
        ) : sales.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />
            {t('instructorFinance.salesEmpty')}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {sales.map((s) => {
              // Бэйджи:
              //   refunded → админ оформил возврат, сумма из баланса вычтена
              //   paid     → деньги уже у тебя в Kaspi
              //   pending  → ты запросил выплату, ждём админа
              //   (else)   → заказ подтверждён, деньги в балансе, нужно
              //              нажать «Запросить выплату» когда захочешь
              const isRefunded = s.status === 'refunded';
              const badge = isRefunded ? (
                <span
                  className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                  title={s.refundReason
                    ? t('instructorFinance.salesBadgeRefundedReason', { reason: s.refundReason })
                    : t('instructorFinance.salesBadgeRefundedNoReason')}
                >
                  {t('instructorFinance.salesBadgeRefunded')}
                </span>
              ) : s.payout?.status === 'paid' ? (
                <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                  {t('instructorFinance.salesBadgePaid')}
                </span>
              ) : s.payout?.status === 'pending' ? (
                <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                  {t('instructorFinance.salesBadgePending')}
                </span>
              ) : (
                <span
                  className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300"
                  title={t('instructorFinance.salesBadgeInBalanceTooltip')}
                >
                  {t('instructorFinance.salesBadgeInBalance')}
                </span>
              );
              return (
                <div key={s.id} className={`px-5 py-3 flex items-center justify-between gap-4 ${isRefunded ? 'opacity-60' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-medium text-gray-900 dark:text-white truncate ${isRefunded ? 'line-through' : ''}`}>
                      {s.course?.title || '—'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      {s.studentName} · {new Date(s.confirmedAt).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      {isRefunded && s.refundedAt && (
                        <> · {t('instructorFinance.salesRefundedAt', { date: new Date(s.refundedAt).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' }) })}</>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-sm font-semibold tabular-nums ${
                      isRefunded
                        ? 'text-red-600 dark:text-red-400 line-through'
                        : 'text-emerald-600 dark:text-emerald-400'
                    }`}>
                      {isRefunded ? '−' : '+'}{fmt(s.instructorShare)}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5 tabular-nums">
                      {t('instructorFinance.salesFromAmount', { amount: fmt(s.amount) })}
                    </div>
                    <div className="mt-1">{badge}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Footer продаж: серверные total/pageCount. «N–M из K» + Pagination. */}
        {!loadingSales && salesTotal > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
              {t('instructorFinance.showing')}{' '}
              <b className="text-gray-700 dark:text-gray-200">
                {(salesPage - 1) * SALES_PAGE_SIZE + 1}
                –
                {Math.min(salesPage * SALES_PAGE_SIZE, salesTotal)}
              </b>{' '}
              {t('instructorFinance.of')} <b className="text-gray-700 dark:text-gray-200">{salesTotal}</b>
              {salesPageCount > 1 && (
                <span className="ml-2 text-gray-400">· {t('instructorFinance.pageOf', { page: salesPage, total: salesPageCount })}</span>
              )}
            </div>
            {salesPageCount > 1 && (
              <Pagination
                page={salesPage}
                pageCount={salesPageCount}
                onChange={(p) => {
                  setSalesPage(p);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* Payout history */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="font-semibold text-gray-900 dark:text-white">{t('instructorFinance.payoutsTitle')}</h2>
        </div>
        {loadingPayouts ? (
          <div className="p-8"><Loading variant="inline" /></div>
        ) : payouts.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('instructorFinance.payoutsEmpty')}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {payouts.map((p) => {
              const meta = PAYOUT_STATUS_META[p.status] || PAYOUT_STATUS_META.pending;
              return (
                <div key={p.id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                      {fmt(p.amount)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {p.kaspiPhone} · {new Date(p.requestedAt).toLocaleDateString('ru-RU')}
                    </div>
                    {p.rejectionReason && (
                      <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {t('instructorFinance.payoutsReason', { reason: p.rejectionReason })}
                      </div>
                    )}
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${COLOR_CLASSES[meta.color]}`}>
                    {t(`instructorFinance.${meta.key}`)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {/* Footer выплат: серверные total/pageCount. «N–M из K» + Pagination. */}
        {!loadingPayouts && payoutsTotal > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
              {t('instructorFinance.showing')}{' '}
              <b className="text-gray-700 dark:text-gray-200">
                {(payoutsPage - 1) * PAYOUTS_PAGE_SIZE + 1}
                –
                {Math.min(payoutsPage * PAYOUTS_PAGE_SIZE, payoutsTotal)}
              </b>{' '}
              {t('instructorFinance.of')} <b className="text-gray-700 dark:text-gray-200">{payoutsTotal}</b>
              {payoutsPageCount > 1 && (
                <span className="ml-2 text-gray-400">· {t('instructorFinance.pageOf', { page: payoutsPage, total: payoutsPageCount })}</span>
              )}
            </div>
            {payoutsPageCount > 1 && (
              <Pagination
                page={payoutsPage}
                pageCount={payoutsPageCount}
                onChange={(p) => {
                  setPayoutsPage(p);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            )}
          </div>
        )}
      </div>

      <PayoutRequestModal
        open={showPayoutModal}
        overview={o}
        onClose={() => setShowPayoutModal(false)}
        onSuccess={() => refetchOverview()}
      />
    </div>
  );
}
